import { cn } from '../../../shared/utils/cn'

export type GroupEditTab = 'details' | 'users' | 'rics' | 'manage'

interface GroupEditTabsProps {
  activeTab: GroupEditTab
  onTabChange: (tab: GroupEditTab) => void
  /** The system group has nothing to manage, so its Manage tab is not offered. */
  tabs: GroupEditTab[]
  /** Counts shown on the tabs so the group's reach is legible without opening them. */
  counts: Partial<Record<GroupEditTab, number>>
  /** Tabs holding unsaved edits, marked with a dot so a pending save is not lost behind a tab. */
  dirtyTabs?: GroupEditTab[]
}

const NAMES: Record<GroupEditTab, string> = {
  details: 'Details',
  users: 'Users',
  rics: 'RICs',
  manage: 'Manage',
}

/** Same tab bar as Edit User, so the two admin edit pages read alike. */
export function GroupEditTabs({ activeTab, onTabChange, tabs, counts, dirtyTabs = [] }: GroupEditTabsProps) {
  return (
    <div className="border-b border-gray-200">
      <nav className="-mb-px flex space-x-8" aria-label="Edit Group Tabs">
        {tabs.map((id) => {
          const isActive = activeTab === id
          const count = counts[id]
          return (
            <button
              key={id}
              type="button"
              onClick={() => onTabChange(id)}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm',
                isActive
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              {NAMES[id]}
              {count !== undefined && (
                <span
                  className={cn(
                    'ml-2 px-2 py-0.5 rounded-full text-xs font-semibold',
                    isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                  )}
                >
                  {count}
                </span>
              )}
              {dirtyTabs.includes(id) && (
                <span
                  className="ml-2 inline-block w-2 h-2 rounded-full bg-amber-500 align-middle"
                  aria-label="Unsaved changes"
                  title="Unsaved changes"
                />
              )}
            </button>
          )
        })}
      </nav>
    </div>
  )
}
